import orangeBgLandscape from "src/static/img/postRsvp/background/orange-bg-landscape.webp";
import orangeBgPortrait from "src/static/img/postRsvp/background/orange-bg-portrait.webp";
import orangeBgSquare from "src/static/img/postRsvp/background/orange-bg-square.webp";
import orangeStampWave from "src/static/img/postRsvp/background/orange-stamp-wave.png";
import paperBackground from "src/static/img/postRsvp/background/paper-background.webp";
import redBgLandscape from "src/static/img/postRsvp/background/red-bg-landscape.webp";
import redBgPortrait from "src/static/img/postRsvp/background/red-bg-portrait.webp";
import redBgSquare from "src/static/img/postRsvp/background/red-bg-square.webp";
import redStampWave from "src/static/img/postRsvp/background/red-stamp-wave.png";
import stampOnOrange from "src/static/img/postRsvp/background/stamp-on-orange.webp";
import stampOnRed from "src/static/img/postRsvp/background/stamp-on-red.webp";
import stampOnYellow from "src/static/img/postRsvp/background/stamp-on-yellow.webp";
import yellowBgLandscape from "src/static/img/postRsvp/background/yellow-bg-landscape.webp";
import yellowBgPortrait from "src/static/img/postRsvp/background/yellow-bg-portrait.webp";
import yellowBgSquare from "src/static/img/postRsvp/background/yellow-bg-square.webp";
import yellowStampWave from "src/static/img/postRsvp/background/yellow-stamp-wave.png";
import excitementDetail from "src/static/img/postRsvp/details/excitement.png";
import instrumentDetail from "src/static/img/postRsvp/details/instrument.png";
import musicDetail from "src/static/img/postRsvp/details/music.png";
import plantDetail from "src/static/img/postRsvp/details/plant.png";
import sparklesDetail from "src/static/img/postRsvp/details/sparkles.png";
import wrenchDetail from "src/static/img/postRsvp/details/wrench.png";
import ginnyDefault from "src/static/img/postRsvp/hackpal/ginny-default.webp";
import ginnySnooze from "src/static/img/postRsvp/hackpal/ginny-snooze.webp";
import ginnySparkle from "src/static/img/postRsvp/hackpal/ginny-sparkle.webp";
import patchDefault from "src/static/img/postRsvp/hackpal/patch-default.webp";
import patchSnooze from "src/static/img/postRsvp/hackpal/patch-snooze.webp";
import patchSparkle from "src/static/img/postRsvp/hackpal/patch-sparkle.webp";
import vinylDefault from "src/static/img/postRsvp/hackpal/vinyl-default.webp";
import vinylSnooze from "src/static/img/postRsvp/hackpal/vinyl-snooze.webp";
import vinylSparkle from "src/static/img/postRsvp/hackpal/vinyl-sparkle.webp";
import ginnyIcon from "src/static/img/postRsvp/icons/ginny-icon.png";
import patchIcon from "src/static/img/postRsvp/icons/patch-icon.png";
import vinylIcon from "src/static/img/postRsvp/icons/vinyl-icon.png";

import {
  Accessory,
  Backdrop,
  CanvasOrientation,
  Effect,
  Feeling,
  Hackpal,
} from "./types";

/**
 * Backdrop stamp scenes are named by their outline colour:
 *   yellow-bg-* = nighttime, orange-bg-* = forest, red-bg-* = mountains
 * (each includes the scalloped stamp outline + environment + wooden sign).
 */
export const BACKDROP_BG: Record<
  Backdrop,
  Record<CanvasOrientation, string>
> = {
  [Backdrop.NIGHTTIME]: {
    [CanvasOrientation.SQUARE]: yellowBgSquare,
    [CanvasOrientation.PORTRAIT]: yellowBgPortrait,
    [CanvasOrientation.LANDSCAPE]: yellowBgLandscape,
  },
  [Backdrop.FOREST]: {
    [CanvasOrientation.SQUARE]: orangeBgSquare,
    [CanvasOrientation.PORTRAIT]: orangeBgPortrait,
    [CanvasOrientation.LANDSCAPE]: orangeBgLandscape,
  },
  [Backdrop.MOUNTAINS]: {
    [CanvasOrientation.SQUARE]: redBgSquare,
    [CanvasOrientation.PORTRAIT]: redBgPortrait,
    [CanvasOrientation.LANDSCAPE]: redBgLandscape,
  },
};

/** Decorative wave that sits bottom-left on the paper, tinted per backdrop. */
export const BACKDROP_WAVE: Record<Backdrop, string> = {
  [Backdrop.NIGHTTIME]: yellowStampWave,
  [Backdrop.FOREST]: orangeStampWave,
  [Backdrop.MOUNTAINS]: redStampWave,
};

export const HACKPAL_IMG: Record<Hackpal, Record<Feeling, string>> = {
  [Hackpal.GINNY]: {
    [Feeling.DEFAULT]: ginnyDefault,
    [Feeling.SPARKLE]: ginnySparkle,
    [Feeling.SNOOZE]: ginnySnooze,
  },
  [Hackpal.PATCH]: {
    [Feeling.DEFAULT]: patchDefault,
    [Feeling.SPARKLE]: patchSparkle,
    [Feeling.SNOOZE]: patchSnooze,
  },
  [Hackpal.VINYL]: {
    [Feeling.DEFAULT]: vinylDefault,
    [Feeling.SPARKLE]: vinylSparkle,
    [Feeling.SNOOZE]: vinylSnooze,
  },
};

export interface BackdropPalette {
  /** Ink stamp / name overlay colour */
  ink: string;
}

export interface HeaderGradientStop {
  offset: number; // 0–1
  color: string;
}

/** Left→right gradient applied independently to each headline line. */
export const HEADER_TEXT_GRADIENT: Record<Backdrop, HeaderGradientStop[]> = {
  [Backdrop.NIGHTTIME]: [
    { offset: 0, color: "#FFF39F" },
    { offset: 0.39, color: "#F6D809" }, // yellow-500
    { offset: 1, color: "#F0DC42" },
  ],
  [Backdrop.FOREST]: [
    { offset: 0, color: "#FFD37B" },
    { offset: 1, color: "#ED6E12" }, // orange-500
  ],
  [Backdrop.MOUNTAINS]: [
    { offset: 0, color: "#D42B2B" },
    { offset: 1, color: "#F3675A" }, // red-400
  ],
};

/** Signage text gradient — same for every backdrop. */
export const SIGN_TEXT_GRADIENT: HeaderGradientStop[] = [
  { offset: 0, color: "#FFF39F" },
  { offset: 0.58, color: "#F6D809" }, // yellow-500
  { offset: 1, color: "#F0DC42" },
];

const gradientStopsCss = (stops: HeaderGradientStop[]): string =>
  `linear-gradient(90deg, ${stops
    .map((s) => `${s.color} ${Math.round(s.offset * 100)}%`)
    .join(", ")})`;

export const headerGradientCss = (backdrop: Backdrop): string =>
  gradientStopsCss(HEADER_TEXT_GRADIENT[backdrop]);

export const SIGN_TEXT_GRADIENT_CSS = gradientStopsCss(SIGN_TEXT_GRADIENT);

// Ink colours sampled from stamp-on-{yellow,orange,red}.webp
// (green-500 / teal-400 / neutral-150).
export const BACKDROP_PALETTE: Record<Backdrop, BackdropPalette> = {
  [Backdrop.NIGHTTIME]: { ink: "#48B058" },
  [Backdrop.FOREST]: { ink: "#00C8C8" },
  [Backdrop.MOUNTAINS]: { ink: "#282020" },
};

export const PAPER_COLOR = "#F5EEDD";
export const PAPER_TEXTURE: string = paperBackground;

export const INK_STAMP_IMG: Record<Backdrop, string> = {
  [Backdrop.NIGHTTIME]: stampOnYellow,
  [Backdrop.FOREST]: stampOnOrange,
  [Backdrop.MOUNTAINS]: stampOnRed,
};

export const ACCESSORY_IMG: Partial<Record<Accessory, string>> = {
  [Accessory.INSTRUMENT]: instrumentDetail,
  [Accessory.PLANT]: plantDetail,
  [Accessory.WRENCH]: wrenchDetail,
};

export const EFFECT_IMG: Partial<Record<Effect, string>> = {
  [Effect.EXCITEMENT]: excitementDetail,
  [Effect.MUSIC]: musicDetail,
  [Effect.SPARKLES]: sparklesDetail,
};

/** Small preview images used as option-card icons for the hackpal picker */
export const HACKPAL_ICON: Record<Hackpal, string> = {
  [Hackpal.GINNY]: ginnyIcon,
  [Hackpal.PATCH]: patchIcon,
  [Hackpal.VINYL]: vinylIcon,
};
